import { ChevronUpIcon } from '@heroicons/react/outline'
import { useWindowScroll } from '@mantine/hooks'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import EarlyKlassTooltip from 'features/klass/components/EarlyKlassTooltip'
import _ from 'lodash'
import { AdditionalList, IAdditionalItem } from 'mocks/kdt'
import { FC, useMemo, useRef, useState } from 'react'

import BasicTimer from '../../components/KlassTimer/BasicTimer'
import MobileTimer from '../../components/KlassTimer/MobileTimer'
import { ICourse, IKDTKlass } from '../../stores/courses'
import { checkInstallmentAmount, DEFAULT_PLAN } from '../../utils/installment'
import ActionButton from './ActionButton'
import KlassInfoHeader from './KlassInfoHeader'
import KlassInfoSection from './KlassInfoSection'

dayjs.extend(isBetween)

export interface IApplyPeriodList {
  [status: string]: {
    [date: string]: string
  }
}

interface ICourseInfoProps {
  course: ICourse | IKDTKlass
  isMobile?: boolean
  submitType: {
    [status: string]: {
      disabled: boolean
      name: string
      period?: string
      handler: () => void
    }
  }
  status: string
  isDelayCourse?: boolean
  applyPeriodList?: IApplyPeriodList
  expiryTimestamp?: number
  isSigned?: boolean
  isSimplifyKDT: boolean
}

interface INextStatus {
  [status: string]: string
}

const CourseInfo: FC<ICourseInfoProps> = ({
  course,
  isMobile,
  submitType,
  status,
  applyPeriodList,
  isDelayCourse,
  expiryTimestamp,
  isSigned,
  isSimplifyKDT,
}) => {
  const {
    id,
    title,
    price,
    shareUrl,
    panelText,
    promotion,
    promotionPrice,
    courseType,
    startedAt,
    applyStartedAt,
    applyEndedAt,
    imageM,
  } = course
  const tooltipContainerRef = useRef(null)
  const curPrice = price.toLocaleString()
  const isOnDemand = useMemo(() => courseType === 'ondemand', [courseType])
  const isWorker = useMemo(() => courseType === 'worker', [courseType])

  const isKDT = useMemo(
    () => courseType === 'kdt' || courseType === 'kdt_plus' || courseType === 'kdt_startup',
    [courseType],
  )

  const [scroll] = useWindowScroll()
  const [openAddition, setOpenAddition] = useState<string | null>(null)

  const handleItemClick = (itemId: string) => {
    setOpenAddition(itemId === openAddition ? null : itemId)
  }

  const isPromotionActive = useMemo(
    () => promotion !== null && (promotion?.promotionRate > 0 || promotion?.promotionAmount > 0),
    [promotion],
  )
  const promotionRate = useMemo(() => {
    if (promotion === null || promotionPrice === null) return 0
    if (promotion.promotionType === 'rate') return promotion.promotionRate
    else if (promotion.promotionType === 'amount') {
      const rate = promotion.promotionAmount / price
      return Math.floor((rate > 1 ? 1 : rate) * 100)
    }
    return 0
  }, [price, promotion, promotionPrice])
  const amountPerInstallment = useMemo(() => {
    if (isPromotionActive && promotionPrice !== null && promotionPrice > 0)
      return (promotionPrice / 5).toLocaleString()
    else return (price / 5).toLocaleString()
  }, [isPromotionActive, price, promotionPrice])

  const curStatus: INextStatus = isSimplifyKDT
    ? {
        early: 'early',
        assignment: 'assignment',
        assignmentReview: 'assignmentReview',
        participation: dayjs().isBefore(startedAt) ? 'participation' : 'ing',
        participationReview: dayjs().isBefore(startedAt) ? 'participationReview' : 'ing',
        ing: 'ing',
        end: 'end',
      }
    : {
        apply: 'apply',
        assignment: 'assignment',
        participation: dayjs().isBefore(startedAt) ? 'participation' : 'ing',
      }

  const nextStatus: INextStatus = isSimplifyKDT
    ? {
        assignmentReview: 'participation',
        assignment: 'participation',
      }
    : {
        apply: 'assignment',
        assignment: 'participation',
      }

  return (
    <>
      {isMobile ? (
        <div className="block border-b lg:hidden">
          <div className="block lg:hidden">
            <div className="mx-4 pb-6 pt-5">
              <KlassInfoHeader
                isMobile
                klassId={id}
                title={title}
                panelText={panelText}
                shareUrl={shareUrl}
                imgUrl={imageM}
              />
              <div className="flex flex-col">
                <KlassInfoSection
                  course={course}
                  isKDT={isKDT}
                  isSimplifyKDT={isSimplifyKDT}
                  isDelayCourse={isDelayCourse}
                  isPreNoticeStatus={submitType[status]?.name?.includes('사전알림 신청')}
                  applyStatus={status}
                  applyPeriodList={applyPeriodList}
                />
                {!isKDT && (
                  <>
                    <div className="flex flex-col space-y-1">
                      <hr className="my-4 border-t" />
                      <div className="flex text-sm text-gray-500">
                        {~~price !== 0 && (
                          <>
                            {isPromotionActive ? (
                              <>
                                <div className="line-through">{curPrice}원</div>
                                {!isOnDemand && (
                                  <span>
                                    &nbsp;/&nbsp;
                                    {promotionPrice !== null && promotionPrice > 0
                                      ? promotionPrice.toLocaleString()
                                      : 0}
                                    원
                                  </span>
                                )}
                              </>
                            ) : (
                              !isOnDemand && <div>{curPrice}원</div>
                            )}
                          </>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-baseline space-x-1">
                          {isPromotionActive ? (
                            <>
                              <span className="text-xl font-bold text-primary-normal">
                                {promotionRate > 0 && promotionRate}%
                              </span>
                              <span className="text-xl font-semibold">
                                {promotionPrice !== null &&
                                  promotionPrice > 0 &&
                                  promotionPrice?.toLocaleString()}
                                원
                              </span>
                            </>
                          ) : (
                            <span className="text-xl font-semibold leading-tight">
                              {~~price === 0 ? '무료' : `${curPrice}원`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          {/* 부가 설명 */}
          <div className="mx-4 flex flex-col gap-y-2 lg:mt-5">
            {AdditionalList[courseType]?.map(addition => {
              const isOpen = openAddition === addition.id
              return (
                <section
                  className="cursor-pointer rounded-lg bg-gray-50 px-4 py-3 last-of-type:mb-6"
                  key={addition.id}
                  onClick={() => handleItemClick(addition.id)}>
                  <div className="flex items-center justify-between gap-x-2">
                    <div className="flex items-center gap-x-2">
                      <div className="flex h-6 w-6 items-center justify-center">
                        <img src={addition.icon} alt={addition.title} />
                      </div>
                      <p className="text-sm font-bold">
                        {addition.boldTitle}
                        <span className="ml-1 inline-block font-normal">{addition.title}</span>
                      </p>
                    </div>
                    <ChevronUpIcon
                      className={`${isOpen ? '' : 'rotate-180 transform'} w-4 text-gray-400`}
                    />
                  </div>
                  {/* open */}
                  {isOpen && (
                    <div className="mt-4 flex flex-col gap-y-1.5">
                      {addition.summary && (
                        <p className="text-xs font-semibold">{addition.summary}</p>
                      )}
                      <p className="text-xs text-gray-500">
                        <span className="font-semibold">{addition.boldDesc}</span>
                        {addition.desc}
                      </p>
                      {addition.reference && (
                        <span className="text-xs text-gray-500">{addition.reference}</span>
                      )}
                    </div>
                  )}
                </section>
              )
            })}
          </div>
          {/* 하단 고정되는 영역 */}
          <div
            className="fixed bottom-0 left-0 z-30 block w-full bg-white shadow-[0px_-1px_4px_0px_rgba(0,0,0,0.05)] lg:hidden"
            style={{ minWidth: 320 }}>
            {isKDT && (
              <>
                {submitType[status]?.name.includes('완료') && (
                  <div className="flex items-center justify-center border-b py-1.5 text-sm font-normal text-gray-500">
                    {status !== 'participation'
                      ? ` ${dayjs(
                          applyPeriodList && applyPeriodList[nextStatus[status]]?.startedAt,
                        ).format('M월 D일')} ${
                          isSimplifyKDT ? '' : `${submitType[status]?.name?.charAt(0)}차`
                        } 합격 발표 `
                      : `${dayjs(startedAt).format('M월 D일')} 훈련이 시작될 `}
                    예정입니다.
                  </div>
                )}
                {!isSigned && dayjs().isAfter(applyEndedAt) && (
                  <div className="flex items-center justify-center border-b py-1.5 text-sm font-normal text-gray-500">
                    이미 지원한 경우 로그인하여 진행내역을 확인하세요.
                  </div>
                )}
              </>
            )}

            <div className="px-4 py-3">
              <div className="flex">
                {(submitType[status]?.period === 'applying' ||
                  (isWorker && dayjs().isBetween(applyStartedAt, applyEndedAt, undefined, '[)'))) &&
                  expiryTimestamp && <MobileTimer expiryTimestamp={expiryTimestamp} />}
                {!_.isEmpty(submitType) && !_.isEmpty(status) && (
                  <>
                    {status === 'early' && (
                      <div
                        ref={tooltipContainerRef}
                        className="absolute left-1/2 top-1 flex items-center justify-center text-gray-500">
                        <EarlyKlassTooltip wrapperElementRef={tooltipContainerRef} />
                      </div>
                    )}
                    <ActionButton
                      klassId={id}
                      disabled={submitType[isKDT ? curStatus[status] : status]?.disabled}
                      handler={submitType[isKDT ? curStatus[status] : status]?.handler}
                      name={submitType[isKDT ? curStatus[status] : status]?.name}
                      isMobile={isMobile}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="px-4 pt-5 lg:rounded-lg lg:border lg:border-gray-300 lg:p-6">
            <KlassInfoHeader
              isMobile={false}
              klassId={id}
              title={title}
              panelText={panelText}
              shareUrl={shareUrl}
              imgUrl={imageM}
            />
            <div className="flex flex-col">
              <KlassInfoSection
                course={course}
                isKDT={isKDT}
                isSimplifyKDT={isSimplifyKDT}
                isDelayCourse={isDelayCourse}
                isPreNoticeStatus={submitType[status]?.name?.includes('사전알림 신청')}
                applyStatus={status}
                applyPeriodList={applyPeriodList}
              />
              {!isKDT && (
                <>
                  <hr className="my-4 border-gray-300" />
                  <div className="flex flex-col space-y-1">
                    <div className="flex text-sm text-gray-500">
                      {~~price !== 0 && (
                        <>
                          {isPromotionActive ? (
                            <>
                              <div className="line-through">{curPrice}원</div>
                              {!isOnDemand && (
                                <span>
                                  &nbsp;/&nbsp;
                                  {promotionPrice !== null && promotionPrice > 0
                                    ? promotionPrice.toLocaleString()
                                    : 0}
                                  원
                                </span>
                              )}
                            </>
                          ) : (
                            !isOnDemand && <div>{curPrice}원</div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="flex items-baseline space-x-1">
                      {isPromotionActive ? (
                        <>
                          <span className="text-xl font-bold text-primary-normal">
                            {promotionRate > 0 && promotionRate}%
                          </span>
                          <span className="text-xl font-semibold">
                            {promotionPrice !== null &&
                              promotionPrice > 0 &&
                              promotionPrice?.toLocaleString()}
                            원
                          </span>
                        </>
                      ) : (
                        <div className="flex items-baseline space-x-1">
                          <span className="text-xl font-semibold leading-tight">
                            {~~price === 0 ? '무료' : `${curPrice}원`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* 결제하기 버튼 */}
            {!_.isEmpty(submitType) && !_.isEmpty(status) && (
              <>
                <div className={`${status === 'early' ? 'relative mt-9' : ''}`}>
                  {status === 'early' && (
                    <div
                      ref={tooltipContainerRef}
                      className="absolute -top-1 left-1/2 flex items-center justify-center text-gray-500">
                      <EarlyKlassTooltip wrapperElementRef={tooltipContainerRef} />
                    </div>
                  )}
                  <ActionButton
                    klassId={id}
                    disabled={submitType[isKDT ? curStatus[status] : status]?.disabled}
                    handler={submitType[isKDT ? curStatus[status] : status]?.handler}
                    name={submitType[isKDT ? curStatus[status] : status]?.name}
                  />
                </div>
                {isKDT && (
                  <>
                    {submitType[status]?.name?.includes('완료') && (
                      <div className="mt-2 text-sm font-normal text-gray-500">
                        &nbsp;∙{' '}
                        {status !== 'participation'
                          ? ` ${dayjs(
                              applyPeriodList && applyPeriodList[nextStatus[status]]?.startedAt,
                            ).format('M월 D일')} ${
                              isSimplifyKDT ? '' : `${submitType[status]?.name?.charAt(0)}차`
                            } 합격 발표 `
                          : `${dayjs(startedAt).format('M월 D일')} 훈련이 시작될 `}
                        예정입니다.
                      </div>
                    )}
                    {!isSigned && dayjs().isAfter(applyEndedAt) && (
                      <div className="mt-2 text-sm font-normal leading-normal text-gray-500">
                        - 이미 지원한 경우 로그인하여 진행내역을 확인하세요.
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          {(submitType[status]?.period === 'applying' ||
            (isWorker && dayjs().isBetween(applyStartedAt, applyEndedAt, undefined, '[)'))) &&
            expiryTimestamp && <BasicTimer expiryTimestamp={expiryTimestamp} />}
          {/* 부가 설명 */}
          <div className="flex flex-col gap-y-2 lg:mt-5">
            {AdditionalList[courseType]?.map(addition => {
              const isOpen = openAddition === addition.id
              return (
                <section
                  className="cursor-pointer rounded-lg bg-gray-50 p-4"
                  key={addition.title}
                  onClick={() => handleItemClick(addition.id)}>
                  <div className="flex items-center justify-between gap-x-2">
                    <div className="flex items-center gap-x-2">
                      <div className="flex h-6 w-6 items-center justify-center">
                        <img src={addition.icon} alt={addition.title} />
                      </div>
                      <p className="text-sm font-bold">
                        {addition.boldTitle}
                        <span className="ml-1 inline-block font-normal">{addition.title}</span>
                      </p>
                    </div>
                    <ChevronUpIcon
                      className={`${isOpen ? '' : 'rotate-180 transform'} w-4 text-gray-400`}
                    />
                  </div>
                  {/* open */}
                  {isOpen && (
                    <div className="mt-4 flex flex-col gap-y-2">
                      {addition.summary && (
                        <p className="text-sm font-semibold">{addition.summary}</p>
                      )}
                      <p className="text-sm text-gray-500">
                        <span className="font-semibold">{addition.boldDesc}</span>
                        {addition.desc}
                      </p>
                      {addition.reference && (
                        <span className="text-sm text-gray-500">{addition.reference}</span>
                      )}
                    </div>
                  )}
                </section>
              )
            })}
          </div>
        </>
      )}
    </>
  )
}

const levelMap = ['입문', '초급', '중급', '고급', '고급이상']

export default CourseInfo
